import { useMemo } from "react";

import { useAtom } from "jotai";
import { AlertTriangle, X } from "lucide-react";

import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { Button } from "src/components/ui/button";
import useDashboard from "src/lib/hooks/useDashboard";
import { useOnboarding } from "src/lib/hooks/useOnboarding";
import { openOnboardingAction } from "src/lib/onboarding/actions";
import { resolveOnboardingPageNotice } from "src/lib/onboarding/buildOnboardingState";

export function OnboardingPageNotice() {
  const { currentPage } = useDashboard();
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);
  const {
    selectedStore,
    onboarding,
    onboardingStates,
    dismissPrompt,
    isStoreAdmin,
    loading,
  } = useOnboarding({ queryOnRender: false });

  const dismissedNoticeIds = useMemo(
    () =>
      new Set(
        onboardingStates
          .filter(({ dismissedAt }) => dismissedAt)
          .map(({ scopeKey, key }) => `${scopeKey}:${key}`)
      ),
    [onboardingStates]
  );

  const notice = resolveOnboardingPageNotice({
    pageId: currentPage?.id,
    selectedStore,
    onboarding,
    dismissedNoticeIds,
    isStoreAdmin,
  });

  if (!notice) {
    return null;
  }

  const dismissNotice = () =>
    dismissPrompt({
      scopeKey: notice.scopeKey,
      key: notice.key,
      payload: {
        pageId: currentPage?.id || null,
      },
    });

  return (
    <div className="border-border/70 bg-card/60 flex flex-col gap-3 rounded-lg border px-3 py-2.5 backdrop-blur-sm sm:flex-row sm:items-center">
      <div className="flex min-w-0 items-start gap-3 sm:flex-1">
        <div className="bg-muted text-primary flex size-8 shrink-0 items-center justify-center rounded-md border border-border/60">
          <AlertTriangle className="size-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{notice.title}</div>
          <div className="text-muted-foreground mt-0.5 text-xs leading-5">
            {notice.description}
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground size-7 shrink-0 sm:hidden"
          disabled={loading}
          onClick={dismissNotice}
        >
          <X className="size-4" />
          <span className="sr-only">Dismiss onboarding notice</span>
        </Button>
      </div>

      <div
        className={`items-center gap-1.5 sm:shrink-0 sm:self-center ${
          notice.action ? "flex" : "hidden sm:flex"
        }`}
      >
        {notice.action && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 flex-1 px-3 sm:flex-none"
            disabled={loading}
            onClick={() =>
              openOnboardingAction({
                action: notice.action,
                setSettingsDialogState,
              })
            }
          >
            {notice.action.label}
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hidden size-7 shrink-0 sm:inline-flex"
          disabled={loading}
          onClick={dismissNotice}
        >
          <X className="size-4" />
          <span className="sr-only">Dismiss onboarding notice</span>
        </Button>
      </div>
    </div>
  );
}
